const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Mock data for development/fallback
const MOCK_DASHBOARD_DATA = {
  totalRequests: 154,
  totalBots: 8,
  activeServices: [
    { type: 'Intelligence', search_term: 'Athens Residential', providers: ['Spitogatos', 'Xrisi Efkeria'], active: true },
    { type: 'News', search_term: 'Real Estate Trends', providers: ['Google News'], active: true },
    { type: 'Price Index', search_term: 'Glyfada', providers: ['Spitogatos'], active: true },
    { type: 'Intelligence', search_term: 'Thessaloniki Commercial', providers: ['Plot.gr'], active: false }
  ],
  averageSPI: 142.5
};

const MOCK_BOTS_DATA = {
  totalBots: 8,
  bots: [
    { 
      name: 'Attica Scraper', 
      link: '#', 
      properties: ['Residential', 'Sale'], 
      search_terms: ['Athens', 'Marousi', 'Kifisia'] 
    },
    { 
      name: 'Island Monitor', 
      link: '#', 
      properties: ['Holiday Home', 'Sale'], 
      search_terms: ['Cyclades', 'Crete'] 
    },
    { 
      name: 'Commercial Watcher', 
      link: '#', 
      properties: ['Commercial', 'Rent'], 
      search_terms: ['Athens Center', 'Piraeus'] 
    },
    { 
      name: 'News Aggregator', 
      link: '#', 
      properties: ['News'], 
      search_terms: ['Market Trends', 'Legislation'] 
    }
  ]
};

export const fetchDashboardData = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/data/dashboard`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.warn('Error fetching dashboard data, falling back to mock data:', error);
    // Return mock data structure matching API response
    return {
      success: true,
      data: MOCK_DASHBOARD_DATA
    };
  }
};

export const fetchBotsData = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/data/bots`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.warn('Error fetching bots data, falling back to mock data:', error);
    // Return mock data structure matching API response
    return {
      success: true,
      data: MOCK_BOTS_DATA
    };
  }
};

export const fetchKnowledgeGraphSources = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/data/knowledge-graph/sources`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.warn('Error fetching knowledge graph sources, falling back to mock data:', error);
    return {
      success: true,
      data: [
        { file_name: 'listing_45.pdf', provider: "Sotheby's", connections_found: 12, url: 'https://cdn.example.com/doc1.pdf' },
        { file_name: 'market_report_jan.pdf', provider: 'Knight Frank', connections_found: 8, url: 'https://cdn.example.com/doc2.pdf' },
        { file_name: 'athens_residential_v2.pdf', provider: 'RE/MAX', connections_found: 15, url: 'https://cdn.example.com/doc3.pdf' },
      ]
    };
  }
};

export const fetchHierarchyTree = async (nodeId, direction = 'outgoing', maxDepth = 1) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/data/knowledge-graph/tree/${nodeId}?direction=${direction}&maxDepth=${maxDepth}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching hierarchy tree:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

// ============================================================================
// NEW ANALYTICS & INSIGHTS API ENDPOINTS
// ============================================================================

export const fetchGraphAnalytics = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/data/knowledge-graph/analytics/overview`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching graph analytics:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

export const fetchCentrality = async (limit = 20) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/data/knowledge-graph/analytics/centrality?limit=${limit}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching centrality:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

export const generateInsight = async (insightType, entityIds = []) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/data/knowledge-graph/insights/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        insightType,
        entityIds
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error generating insight:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

export const fetchSavedInsights = async (insightType, limit = 20, offset = 0) => {
  try {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    if (insightType) params.append('insightType', insightType);

    const response = await fetch(`${API_BASE_URL}/api/data/knowledge-graph/insights/saved?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching saved insights:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

// ============================================================================
// STEP 5: KNOWLEDGE GRAPH COMMUNITY-FIRST NAVIGATION
// ============================================================================

export const fetchCommunities = async (search = '', level = 0, limit = 50, offset = 0) => {
  try {
    const params = new URLSearchParams({ level, limit, offset });
    if (search) params.append('search', search);

    const response = await fetch(`${API_BASE_URL}/api/data/knowledge-graph/communities?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching communities:', error);
    return { success: false, message: error.message };
  }
};

export const fetchCommunityDetail = async (communityId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/data/knowledge-graph/community/${communityId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching community detail:', error);
    return { success: false, message: error.message };
  }
};

// ============================================================================
// STEP 6: SEARCH & DISCOVERY
// ============================================================================

export const searchKnowledgeGraphMultiMode = async (query, mode = 'communities', limit = 10) => {
  try {
    const params = new URLSearchParams({ query, mode, limit });

    const response = await fetch(`${API_BASE_URL}/api/data/knowledge-graph/search?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error in multi-mode search:', error);
    return { success: false, message: error.message };
  }
};

export const fetchEvidence = async (query, limit = 10) => {
  try {
    const params = new URLSearchParams({ query, limit });

    const response = await fetch(`${API_BASE_URL}/api/data/knowledge-graph/evidence?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching evidence:', error);
    return { success: false, message: error.message };
  }
};

export const discoverConnections = async ({ nodeId, attribute, minScore = 0.5, limit = 20 } = {}) => {
  try {
    const params = new URLSearchParams({ minScore, limit });
    if (nodeId) params.append('nodeId', nodeId);
    if (attribute) params.append('attribute', attribute);

    const response = await fetch(`${API_BASE_URL}/api/data/knowledge-graph/discover/connections?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    // Always try to parse the JSON body — the backend sends structured errors too
    let data;
    try {
      data = await response.json();
    } catch {
      throw new Error(`HTTP error! status: ${response.status} (non-JSON response)`);
    }

    if (!response.ok) {
      // Return the structured error so the UI can show the real message
      return {
        success: false,
        message: data?.message || `Request failed with status ${response.status}`,
        status: response.status,
      };
    }

    return data;
  } catch (error) {
    console.error('Error discovering connections:', error);
    return { success: false, message: error.message };
  }
};

// ============================================================================
// STEP 7: USER ITEMS INTELLIGENCE (PDF <-> GRAPH)
// ============================================================================

export const fetchDocumentImpact = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/data/knowledge-graph/user-items/impact`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching document impact:', error);
    return { success: false, message: error.message };
  }
};

export const fetchDocumentNetwork = async (minSharedEntities = 2) => {
  try {
    const params = new URLSearchParams({ minSharedEntities });

    const response = await fetch(`${API_BASE_URL}/api/data/knowledge-graph/user-items/connections?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching document network:', error);
    return { success: false, message: error.message };
  }
};

export const fetchKnowledgeTimeline = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/data/knowledge-graph/user-items/timeline`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching knowledge timeline:', error);
    return { success: false, message: error.message };
  }
};

export const fetchDocumentCommonality = async (doc1, doc2) => {
  try {
    const params = new URLSearchParams({ doc1, doc2 });

    const response = await fetch(`${API_BASE_URL}/api/data/knowledge-graph/user-items/commonality?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching document commonality:', error);
    return { success: false, message: error.message };
  }
};

export const fetchClusterPairs = async (minSharedNeighbors = 3) => {
  try {
    const params = new URLSearchParams({ minSharedNeighbors });

    const response = await fetch(`${API_BASE_URL}/api/data/knowledge-graph/analytics/clusters?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching cluster pairs:', error);
    return { success: false, message: error.message };
  }
};

// ============================================================================
// OMNI SEARCH
// ============================================================================

export const omniSearch = async (q, depth = 1, limit = 30) => {
  try {
    const params = new URLSearchParams({ q, depth, limit });
    const response = await fetch(`${API_BASE_URL}/api/data/knowledge-graph/omni-search?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error in omni search:', error);
    return { success: false, message: error.message };
  }
};

// ============================================================================
// WHITEBOARD API
// ============================================================================

export const fetchBoards = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/whiteboard/boards`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching boards:', error);
    return { success: false, message: error.message };
  }
};

export const createBoard = async (payload) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/whiteboard/boards`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error creating board:', error);
    return { success: false, message: error.message };
  }
};

export const deleteBoard = async (boardId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/whiteboard/boards/${boardId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error deleting board:', error);
    return { success: false, message: error.message };
  }
};

export const fetchBoardGraph = async (boardId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/whiteboard/boards/${boardId}/graph`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching board graph:', error);
    return { success: false, message: error.message };
  }
};

export const fetchBoardConnections = async (boardId, { type, minConfidence, includeHidden = false } = {}) => {
  try {
    const params = new URLSearchParams({ includeHidden });
    if (type) params.append('type', type);
    if (minConfidence != null) params.append('minConfidence', minConfidence);
    const response = await fetch(`${API_BASE_URL}/api/whiteboard/boards/${boardId}/connections?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching board connections:', error);
    return { success: false, message: error.message };
  }
};

export const createConnection = async (boardId, payload) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/whiteboard/boards/${boardId}/connections`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error creating connection:', error);
    return { success: false, message: error.message };
  }
};

export const patchConnection = async (boardId, connectionId, payload) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/whiteboard/boards/${boardId}/connections/${connectionId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error patching connection:', error);
    return { success: false, message: error.message };
  }
};

export const updateBoardItem = async (boardId, itemId, payload) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/whiteboard/boards/${boardId}/items/${itemId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    // Parse JSON even on error so we get the server's structured message
    let data;
    try { data = await response.json(); } catch { data = {}; }
    if (!response.ok) {
      return { success: false, message: data?.message || `HTTP ${response.status}`, status: response.status };
    }
    return data;
  } catch (error) {
    console.error('Error updating board item:', error);
    return { success: false, message: error.message };
  }
};

export const deleteBoardItem = async (boardId, itemId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/whiteboard/boards/${boardId}/items/${itemId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error deleting board item:', error);
    return { success: false, message: error.message };
  }
};

export const generateWhiteboardInsights = async (boardId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/whiteboard/boards/${boardId}/insights`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    let data;
    try { data = await response.json(); } catch { data = {}; }
    if (!response.ok) return { success: false, message: data?.message || `HTTP ${response.status}`, status: response.status };
    return data;
  } catch (error) {
    console.error('Error generating whiteboard insights:', error);
    return { success: false, message: error.message };
  }
};

export const getWhiteboardInsights = async (boardId, { includeExpired = false, limit = 10, offset = 0 } = {}) => {
  try {
    const params = new URLSearchParams({
      includeExpired: String(includeExpired),
      limit: String(limit),
      offset: String(offset),
    });
    const response = await fetch(`${API_BASE_URL}/api/whiteboard/boards/${boardId}/insights?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching whiteboard insights:', error);
    return { success: false, message: error.message };
  }
};

export const fetchAllWhiteboardInsights = async (perBoardLimit = 5) => {
  try {
    const boardsRes = await fetchBoards();
    if (!boardsRes.success || !Array.isArray(boardsRes.data) || boardsRes.data.length === 0) {
      return { success: true, data: [] };
    }
    const boards = boardsRes.data;
    const insightArrays = await Promise.all(
      boards.map(async (board) => {
        const res = await getWhiteboardInsights(board.id, { limit: perBoardLimit });
        if (res.success && Array.isArray(res.data) && res.data.length > 0) {
          return res.data.map(item => ({
            ...item,
            insightType: 'whiteboard_insight',
            boardId: board.id,
            boardName: board.name,
            createdAt: item.created_at || item.createdAt,
          }));
        }
        return [];
      })
    );
    return { success: true, data: insightArrays.flat() };
  } catch (error) {
    console.error('Error fetching all whiteboard insights:', error);
    return { success: false, message: error.message };
  }
};

export const addBoardItem = async (boardId, payload) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/whiteboard/boards/${boardId}/items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    // Parse JSON even on error so we get the server's structured message
    let data;
    try { data = await response.json(); } catch { data = {}; }
    if (!response.ok) {
      return { success: false, message: data?.message || `HTTP ${response.status}`, status: response.status };
    }
    return data;
  } catch (error) {
    console.error('Error adding board item:', error);
    return { success: false, message: error.message };
  }
};

// ============================================================================
// ENTITY CONTEXT
// ============================================================================

export const getEntityContext = async (nodeId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/data/knowledge-graph/entity/${nodeId}/context`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching entity context:', error);
    return { success: false, message: error.message };
  }
};

export const fetchPdfList = async (limit = 100, offset = 0) => {
  try {
    const params = new URLSearchParams({ limit, offset });

    const response = await fetch(`${API_BASE_URL}/api/data/pdf?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching PDF list:', error);
    return { success: false, message: error.message };
  }
};
